import React, { useEffect, useState } from 'react';
import {
  getFinancialDashboard,
  getProfitLossStatement,
  getBalanceSheet,
  getCashFlowStatement,
  getFinancialRatios,
  getVatReconciliation,
  getCreditNotes,
  getDebitNotes,
  createCreditNote,
  createDebitNote,
  getCompanies,
  getInvoices,
  getFinancialTrends,
  getConsolidatedDashboard,
  recordPayroll,
  getPayroll,
  postPayrollToGL,
  recordCommission,
  getCommissions,
  getCommissionSummary
} from '../lib/api';
import {
  LineChart as RechartsLine,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Building2,
  Receipt,
  CreditCard,
  PieChart,
  BarChart3,
  Activity,
  AlertTriangle,
  CheckCircle2,
  FileText,
  RefreshCw,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Scale,
  Target,
  Clock,
  Percent,
  Calculator,
  Landmark,
  Users,
  Briefcase,
  LineChart
} from 'lucide-react';
import { toast } from 'sonner';

// Helper to format currency
const formatCurrency = (value, decimals = 0) => {
  if (value === undefined || value === null) return '0';
  return value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

// Helper to get trend color
const getTrendColor = (value, benchmark, inverse = false) => {
  if (inverse) {
    return value <= benchmark ? 'text-green-600' : 'text-red-600';
  }
  return value >= benchmark ? 'text-green-600' : 'text-amber-600';
};

export const FinancialDashboardPage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);

  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [consolidatedData, setConsolidatedData] = useState(null);

  // Reports data
  const [plStatement, setPlStatement] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [ratios, setRatios] = useState(null);
  const [vatReport, setVatReport] = useState(null);

  // Period selection
  const [periodFrom, setPeriodFrom] = useState(`${new Date().getFullYear()}-01`);
  const [periodTo, setPeriodTo] = useState(new Date().toISOString().slice(0, 7));
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  // Payroll & Commissions
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [commissionRecords, setCommissionRecords] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState(null);
  const [payrollDialogOpen, setPayrollDialogOpen] = useState(false);
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [newPayroll, setNewPayroll] = useState({
    period: new Date().toISOString().slice(0, 7),
    employee_count: 0,
    total_gross: 0,
    basic_salary: 0,
    housing_allowance: 0,
    transport_allowance: 0,
    other_deductions: 0
  });
  const [newCommission, setNewCommission] = useState({
    salesperson_id: '',
    salesperson_name: '',
    period: new Date().toISOString().slice(0, 7),
    base_amount: 0,
    commission_rate: 5,
    bonus_amount: 0,
    notes: ''
  });

  // Credit/Debit Notes
  const [creditNotes, setCreditNotes] = useState([]);
  const [debitNotes, setDebitNotes] = useState([]);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteType, setNoteType] = useState('credit');
  const [invoices, setInvoices] = useState([]);
  const [newNote, setNewNote] = useState({
    invoice_id: '',
    amount: 0,
    reason: '',
    reason_code: 'OTHER',
    description: ''
  });

  useEffect(() => {
    loadCompanies();
    loadConsolidated();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadDashboard();
      loadTrends();
      loadInvoices();
      loadPayroll();
      loadCommissions();
    }
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await getFinancialDashboard(selectedCompany);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrends = async () => {
    try {
      const response = await getFinancialTrends(selectedCompany, 12);
      setTrendData(response.data);
    } catch (error) {
      console.error('Failed to load trends:', error);
    }
  };

  const loadConsolidated = async () => {
    try {
      const response = await getConsolidatedDashboard();
      setConsolidatedData(response.data);
    } catch (error) {
      console.error('Failed to load consolidated data:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await getInvoices(selectedCompany);
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to load invoices:', error);
    }
  };

  const loadPayroll = async () => {
    try {
      const response = await getPayroll(selectedCompany);
      setPayrollRecords(response.data);
    } catch (error) {
      console.error('Failed to load payroll:', error);
    }
  };

  const loadCommissions = async () => {
    try {
      const [commRes, summaryRes] = await Promise.all([
        getCommissions(selectedCompany),
        getCommissionSummary(selectedCompany)
      ]);
      setCommissionRecords(commRes.data);
      setCommissionSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load commissions:', error);
    }
  };

  const handleRecordPayroll = async () => {
    try {
      await recordPayroll({
        company_id: selectedCompany,
        ...newPayroll
      });
      toast.success('Payroll recorded successfully');
      setPayrollDialogOpen(false);
      loadPayroll();
      setNewPayroll({
        period: new Date().toISOString().slice(0, 7),
        employee_count: 0,
        total_gross: 0,
        basic_salary: 0,
        housing_allowance: 0,
        transport_allowance: 0,
        other_deductions: 0
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payroll');
    }
  };

  const handlePostPayroll = async (payrollId) => {
    try {
      const response = await postPayrollToGL(payrollId);
      toast.success(`Payroll posted: ${response.data.journal_number}`);
      loadPayroll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post payroll');
    }
  };

  const handleRecordCommission = async () => {
    try {
      await recordCommission({
        company_id: selectedCompany,
        ...newCommission
      });
      toast.success('Commission recorded successfully');
      setCommissionDialogOpen(false);
      loadCommissions();
      setNewCommission({
        salesperson_id: '',
        salesperson_name: '',
        period: new Date().toISOString().slice(0, 7),
        base_amount: 0,
        commission_rate: 5,
        bonus_amount: 0,
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record commission');
    }
  };

  const loadProfitLoss = async () => {
    setLoading(true);
    try {
      const response = await getProfitLossStatement(selectedCompany, periodFrom, periodTo);
      setPlStatement(response.data);
    } catch (error) {
      toast.error('Failed to load P&L statement');
    } finally {
      setLoading(false);
    }
  };

  const loadBalanceSheet = async () => {
    setLoading(true);
    try {
      const response = await getBalanceSheet(selectedCompany, asOfDate);
      setBalanceSheet(response.data);
    } catch (error) {
      toast.error('Failed to load balance sheet');
    } finally {
      setLoading(false);
    }
  };

  const loadCashFlow = async () => {
    setLoading(true);
    try {
      const response = await getCashFlowStatement(selectedCompany, periodFrom, periodTo);
      setCashFlow(response.data);
    } catch (error) {
      toast.error('Failed to load cash flow statement');
    } finally {
      setLoading(false);
    }
  };

  const loadRatios = async () => {
    setLoading(true);
    try {
      const response = await getFinancialRatios(selectedCompany, asOfDate);
      setRatios(response.data);
    } catch (error) {
      toast.error('Failed to load ratio analysis');
    } finally {
      setLoading(false);
    }
  };

  const loadVatReport = async () => {
    setLoading(true);
    try {
      const response = await getVatReconciliation(selectedCompany, periodTo);
      setVatReport(response.data);
    } catch (error) {
      toast.error('Failed to load VAT reconciliation');
    } finally {
      setLoading(false);
    }
  };

  const loadCreditDebitNotes = async () => {
    try {
      const [cnRes, dnRes] = await Promise.all([
        getCreditNotes(selectedCompany),
        getDebitNotes(selectedCompany)
      ]);
      setCreditNotes(cnRes.data);
      setDebitNotes(dnRes.data);
    } catch (error) {
      console.error('Failed to load notes:', error);
    }
  };

  const handleCreateNote = async () => {
    try {
      if (noteType === 'credit') {
        await createCreditNote({
          company_id: selectedCompany,
          ...newNote
        });
        toast.success('Credit note created');
      } else {
        await createDebitNote({
          company_id: selectedCompany,
          ...newNote
        });
        toast.success('Debit note created');
      }
      setNoteDialogOpen(false);
      loadCreditDebitNotes();
      setNewNote({
        invoice_id: '',
        amount: 0,
        reason: '',
        reason_code: 'OTHER',
        description: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create note');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="financial-dashboard-title">
            Financial Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Executive Overview & Financial Reports
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-[200px]">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Select Company" />
            </SelectTrigger>
            <SelectContent>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadDashboard}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="dashboard" data-testid="dashboard-tab">Dashboard</TabsTrigger>
          <TabsTrigger value="trends" data-testid="trends-tab">Trends</TabsTrigger>
          <TabsTrigger value="consolidated" data-testid="consolidated-tab">Group</TabsTrigger>
          <TabsTrigger value="pnl" data-testid="pnl-tab">P&L</TabsTrigger>
          <TabsTrigger value="balance-sheet" data-testid="bs-tab">Balance Sheet</TabsTrigger>
          <TabsTrigger value="ratios" data-testid="ratios-tab">Ratios</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="payroll-tab">Payroll</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="commissions-tab">Commissions</TabsTrigger>
          <TabsTrigger value="adjustments" data-testid="adjustments-tab">Adjustments</TabsTrigger>
        </TabsList>

        {/* Executive Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {dashboardData ? (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-700">Revenue YTD</span>
                  </div>
                  <p className="text-3xl font-bold text-green-800">
                    {formatCurrency(dashboardData.income_statement?.revenue_ytd)}
                  </p>
                  <p className="text-xs text-green-600 mt-1">SAR</p>
                </Card>

                <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-blue-600" />
                    <span className="text-sm text-blue-700">Net Income YTD</span>
                  </div>
                  <p className="text-3xl font-bold text-blue-800">
                    {formatCurrency(dashboardData.income_statement?.net_income_ytd)}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    {dashboardData.income_statement?.net_margin_pct?.toFixed(1)}% margin
                  </p>
                </Card>

                <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet className="h-5 w-5 text-purple-600" />
                    <span className="text-sm text-purple-700">Cash Position</span>
                  </div>
                  <p className="text-3xl font-bold text-purple-800">
                    {formatCurrency(dashboardData.cash_position?.cash_balance)}
                  </p>
                  <p className="text-xs text-purple-600 mt-1">SAR available</p>
                </Card>

                <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt className="h-5 w-5 text-amber-600" />
                    <span className="text-sm text-amber-700">Accounts Receivable</span>
                  </div>
                  <p className="text-3xl font-bold text-amber-800">
                    {formatCurrency(dashboardData.cash_position?.accounts_receivable)}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">Outstanding invoices</p>
                </Card>
              </div>

              {/* Second Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* AR Aging */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    AR Aging
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Current</span>
                      <span className="font-mono font-semibold text-green-600">
                        {formatCurrency(dashboardData.cash_position?.ar_aging?.current)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">30 Days</span>
                      <span className="font-mono font-semibold text-amber-600">
                        {formatCurrency(dashboardData.cash_position?.ar_aging?.['30_days'])}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">60 Days</span>
                      <span className="font-mono font-semibold text-orange-600">
                        {formatCurrency(dashboardData.cash_position?.ar_aging?.['60_days'])}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">90+ Days</span>
                      <span className="font-mono font-semibold text-red-600">
                        {formatCurrency(dashboardData.cash_position?.ar_aging?.['90_plus'])}
                      </span>
                    </div>
                  </div>
                </Card>

                {/* Debt Position */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Landmark className="h-5 w-5" />
                    Debt Position
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Total Debt</span>
                      <span className="font-mono font-semibold">
                        {formatCurrency(dashboardData.debt_position?.total_debt)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Monthly Debt Service</span>
                      <span className="font-mono font-semibold text-red-600">
                        {formatCurrency(dashboardData.debt_position?.monthly_debt_service)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Active Loans</span>
                      <span className="font-mono font-semibold">
                        {dashboardData.debt_position?.active_loans}
                      </span>
                    </div>
                  </div>
                </Card>

                {/* Assets */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Fixed Assets
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Total Cost</span>
                      <span className="font-mono font-semibold">
                        {formatCurrency(dashboardData.assets?.total_fixed_assets)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Net Book Value</span>
                      <span className="font-mono font-semibold text-blue-600">
                        {formatCurrency(dashboardData.assets?.net_book_value)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Asset Count</span>
                      <span className="font-mono font-semibold">
                        {dashboardData.assets?.asset_count}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Alerts */}
              {(dashboardData.alerts?.overdue_ar > 0 || dashboardData.alerts?.high_debt_ratio || dashboardData.alerts?.low_cash) && (
                <Card className="p-4 bg-red-50 border-red-200">
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-red-800">
                    <AlertTriangle className="h-5 w-5" />
                    Financial Alerts
                  </h3>
                  <div className="space-y-2">
                    {dashboardData.alerts?.overdue_ar > 0 && (
                      <div className="flex items-center gap-2 text-sm text-red-700">
                        <ArrowDownRight className="h-4 w-4" />
                        <span>Overdue AR (90+ days): {formatCurrency(dashboardData.alerts.overdue_ar)} SAR</span>
                      </div>
                    )}
                    {dashboardData.alerts?.high_debt_ratio && (
                      <div className="flex items-center gap-2 text-sm text-red-700">
                        <ArrowDownRight className="h-4 w-4" />
                        <span>High debt-to-asset ratio detected</span>
                      </div>
                    )}
                    {dashboardData.alerts?.low_cash && (
                      <div className="flex items-center gap-2 text-sm text-red-700">
                        <ArrowDownRight className="h-4 w-4" />
                        <span>Cash balance below monthly debt service requirement</span>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Operations Summary */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Operations Summary
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Gross Margin</p>
                    <p className={`text-2xl font-bold ${getTrendColor(dashboardData.income_statement?.gross_margin_pct, 30)}`}>
                      {dashboardData.income_statement?.gross_margin_pct?.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Operating Margin</p>
                    <p className={`text-2xl font-bold ${getTrendColor(dashboardData.operations?.operating_margin, 15)}`}>
                      {dashboardData.operations?.operating_margin?.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Costs YTD</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(dashboardData.operations?.total_costs_ytd)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Net Margin</p>
                    <p className={`text-2xl font-bold ${getTrendColor(dashboardData.income_statement?.net_margin_pct, 10)}`}>
                      {dashboardData.income_statement?.net_margin_pct?.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </Card>
            </>
          ) : (
            <Card className="p-12 text-center">
              <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Loading Dashboard...</h3>
              <p className="text-muted-foreground">Fetching financial metrics</p>
            </Card>
          )}
        </TabsContent>

        {/* Profit & Loss Tab */}
        <TabsContent value="pnl" className="space-y-4">
          <Card className="p-4">
            <div className="flex gap-4 items-end">
              <div>
                <Label>Period From</Label>
                <Input type="month" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} />
              </div>
              <div>
                <Label>Period To</Label>
                <Input type="month" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
              </div>
              <Button onClick={loadProfitLoss} disabled={loading}>
                <FileText className="h-4 w-4 mr-2" />
                Generate P&L
              </Button>
            </div>
          </Card>

          {plStatement && (
            <Card className="p-6">
              <h2 className="text-xl font-bold mb-4">Profit & Loss Statement</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Period: {plStatement.period?.from} to {plStatement.period?.to}
              </p>

              <div className="space-y-4">
                {/* Revenue */}
                <div className="border-b pb-4">
                  <div className="flex justify-between items-center font-semibold text-lg">
                    <span>Revenue</span>
                    <span className="text-green-600">{formatCurrency(plStatement.revenue?.total)}</span>
                  </div>
                </div>

                {/* COGS */}
                <div className="border-b pb-4">
                  <div className="flex justify-between items-center">
                    <span>Cost of Goods Sold</span>
                    <span className="text-red-600">({formatCurrency(plStatement.cost_of_goods_sold?.total)})</span>
                  </div>
                </div>

                {/* Gross Profit */}
                <div className="border-b pb-4 bg-slate-50 -mx-6 px-6 py-2">
                  <div className="flex justify-between items-center font-semibold">
                    <span>Gross Profit</span>
                    <div className="text-right">
                      <span className="text-lg">{formatCurrency(plStatement.gross_profit?.amount)}</span>
                      <Badge className="ml-2 bg-green-100 text-green-800">{plStatement.gross_profit?.margin_pct}%</Badge>
                    </div>
                  </div>
                </div>

                {/* Operating Expenses */}
                <div className="border-b pb-4">
                  <div className="flex justify-between items-center">
                    <span>Operating Expenses</span>
                    <span className="text-red-600">({formatCurrency(plStatement.operating_expenses?.total)})</span>
                  </div>
                </div>

                {/* Operating Income */}
                <div className="border-b pb-4 bg-blue-50 -mx-6 px-6 py-2">
                  <div className="flex justify-between items-center font-semibold">
                    <span>Operating Income (EBIT)</span>
                    <div className="text-right">
                      <span className="text-lg">{formatCurrency(plStatement.operating_income?.amount)}</span>
                      <Badge className="ml-2 bg-blue-100 text-blue-800">{plStatement.operating_income?.margin_pct}%</Badge>
                    </div>
                  </div>
                </div>

                {/* Net Income */}
                <div className="bg-green-50 -mx-6 px-6 py-3 rounded-b-lg">
                  <div className="flex justify-between items-center font-bold text-lg">
                    <span>Net Income</span>
                    <div className="text-right">
                      <span className="text-2xl text-green-700">{formatCurrency(plStatement.net_income?.amount)}</span>
                      <Badge className="ml-2 bg-green-200 text-green-900">{plStatement.net_income?.margin_pct}%</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* Balance Sheet Tab */}
        <TabsContent value="balance-sheet" className="space-y-4">
          <Card className="p-4">
            <div className="flex gap-4 items-end">
              <div>
                <Label>As of Date</Label>
                <Input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
              </div>
              <Button onClick={loadBalanceSheet} disabled={loading}>
                <Scale className="h-4 w-4 mr-2" />
                Generate Balance Sheet
              </Button>
            </div>
          </Card>

          {balanceSheet && (
            <div className="grid grid-cols-2 gap-6">
              {/* Assets */}
              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4">Assets</h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-sm text-muted-foreground mb-2">Current Assets</h3>
                    {balanceSheet.assets?.current_assets?.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span>{item.account_name}</span>
                        <span className="font-mono">{formatCurrency(item.balance)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-semibold border-t pt-1">
                      <span>Total Current Assets</span>
                      <span className="font-mono">{formatCurrency(balanceSheet.assets?.current_assets?.total)}</span>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-sm text-muted-foreground mb-2">Fixed Assets</h3>
                    {balanceSheet.assets?.fixed_assets?.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span>{item.account_name}</span>
                        <span className="font-mono">{formatCurrency(item.balance)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-semibold border-t pt-1">
                      <span>Net Fixed Assets</span>
                      <span className="font-mono">{formatCurrency(balanceSheet.assets?.fixed_assets?.total)}</span>
                    </div>
                  </div>

                  <div className="bg-blue-50 -mx-6 px-6 py-3 mt-4">
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total Assets</span>
                      <span className="font-mono text-blue-700">{formatCurrency(balanceSheet.assets?.total_assets)}</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Liabilities & Equity */}
              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4">Liabilities & Equity</h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-sm text-muted-foreground mb-2">Current Liabilities</h3>
                    {balanceSheet.liabilities?.current_liabilities?.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span>{item.account_name}</span>
                        <span className="font-mono">{formatCurrency(item.balance)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-semibold border-t pt-1">
                      <span>Total Current Liabilities</span>
                      <span className="font-mono">{formatCurrency(balanceSheet.liabilities?.current_liabilities?.total)}</span>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-sm text-muted-foreground mb-2">Long-term Liabilities</h3>
                    {balanceSheet.liabilities?.long_term_liabilities?.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span>{item.account_name}</span>
                        <span className="font-mono">{formatCurrency(item.balance)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-semibold border-t pt-1">
                      <span>Total Long-term Liabilities</span>
                      <span className="font-mono">{formatCurrency(balanceSheet.liabilities?.long_term_liabilities?.total)}</span>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-sm text-muted-foreground mb-2">Equity</h3>
                    {balanceSheet.equity?.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span>{item.account_name}</span>
                        <span className="font-mono">{formatCurrency(item.balance)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-semibold border-t pt-1">
                      <span>Total Equity</span>
                      <span className="font-mono">{formatCurrency(balanceSheet.equity?.total_equity)}</span>
                    </div>
                  </div>

                  <div className="bg-blue-50 -mx-6 px-6 py-3 mt-4">
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total Liabilities & Equity</span>
                      <span className="font-mono text-blue-700">{formatCurrency(balanceSheet.total_liabilities_and_equity)}</span>
                    </div>
                    {balanceSheet.is_balanced && (
                      <div className="flex items-center gap-1 text-green-600 text-sm mt-1">
                        <CheckCircle2 className="h-4 w-4" />
                        <span>Balance sheet is balanced</span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Ratios Tab */}
        <TabsContent value="ratios" className="space-y-4">
          <Card className="p-4">
            <div className="flex gap-4 items-end">
              <div>
                <Label>As of Date</Label>
                <Input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
              </div>
              <Button onClick={loadRatios} disabled={loading}>
                <Target className="h-4 w-4 mr-2" />
                Calculate Ratios
              </Button>
            </div>
          </Card>

          {ratios && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Profitability */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  Profitability
                </h3>
                <div className="space-y-3">
                  {Object.entries(ratios.profitability || {}).map(([key, data]) => (
                    <div key={key}>
                      <div className="flex justify-between items-center">
                        <span className="text-sm capitalize">{key.replace('_', ' ')}</span>
                        <span className={`font-bold ${getTrendColor(data.value, parseFloat(data.benchmark))}`}>
                          {data.value}{data.unit}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">Benchmark: {data.benchmark}</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Returns */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Percent className="h-5 w-5 text-blue-600" />
                  Returns
                </h3>
                <div className="space-y-3">
                  {Object.entries(ratios.returns || {}).map(([key, data]) => (
                    <div key={key}>
                      <div className="flex justify-between items-center">
                        <span className="text-sm uppercase">{key}</span>
                        <span className={`font-bold ${getTrendColor(data.value, parseFloat(data.benchmark))}`}>
                          {data.value}{data.unit}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">Benchmark: {data.benchmark}</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Liquidity */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-purple-600" />
                  Liquidity
                </h3>
                <div className="space-y-3">
                  {Object.entries(ratios.liquidity || {}).map(([key, data]) => (
                    <div key={key}>
                      <div className="flex justify-between items-center">
                        <span className="text-sm capitalize">{key.replace('_', ' ')}</span>
                        <span className={`font-bold ${getTrendColor(data.value, parseFloat(data.benchmark))}`}>
                          {data.value}{data.unit}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">Benchmark: {data.benchmark}</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Leverage */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Scale className="h-5 w-5 text-amber-600" />
                  Leverage
                </h3>
                <div className="space-y-3">
                  {Object.entries(ratios.leverage || {}).map(([key, data]) => (
                    <div key={key}>
                      <div className="flex justify-between items-center">
                        <span className="text-sm uppercase">{key.replace('_', ' ')}</span>
                        <span className={`font-bold ${key === 'dscr' ? getTrendColor(data.value, parseFloat(data.benchmark)) : getTrendColor(data.value, parseFloat(data.benchmark), true)}`}>
                          {data.value}{data.unit}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">Benchmark: {data.benchmark}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          <Card className="p-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <LineChart className="h-6 w-6" />
                12-Month Financial Trends
              </h2>
              <Button variant="outline" onClick={loadTrends} disabled={loading}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </Card>

          {trendData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <p className="text-sm text-green-700">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-800">{formatCurrency(trendData.summary?.total_revenue)}</p>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                  <p className="text-sm text-red-700">Total Costs</p>
                  <p className="text-2xl font-bold text-red-800">{formatCurrency(trendData.summary?.total_costs)}</p>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <p className="text-sm text-blue-700">Total Margin</p>
                  <p className="text-2xl font-bold text-blue-800">{formatCurrency(trendData.summary?.total_margin)}</p>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <p className="text-sm text-purple-700">Avg Margin %</p>
                  <p className="text-2xl font-bold text-purple-800">{trendData.summary?.avg_margin_pct?.toFixed(1)}%</p>
                </Card>
              </div>

              {/* Trend Charts */}
              <div className="grid grid-cols-2 gap-4">
                {/* Revenue vs Costs Area Chart */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Revenue vs Costs</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={trendData.trends}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
                        </linearGradient>
                        <linearGradient id="colorCosts" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      />
                      <Legend />
                      <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#22c55e" fillOpacity={1} fill="url(#colorRevenue)" />
                      <Area type="monotone" dataKey="costs" name="Costs" stroke="#ef4444" fillOpacity={1} fill="url(#colorCosts)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>

                {/* Margin Trend Line Chart */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Margin Trend</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={trendData.trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip 
                        formatter={(value, name) => name === 'Margin %' ? `${value}%` : formatCurrency(value)}
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="margin" name="Margin (SAR)" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="margin_pct" name="Margin %" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6', strokeWidth: 2 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Card>
              </div>

              {/* Trend Table */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4">Monthly Breakdown</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Period</th>
                        <th className="text-right py-2 px-3">Revenue</th>
                        <th className="text-right py-2 px-3">Costs</th>
                        <th className="text-right py-2 px-3">Margin</th>
                        <th className="text-right py-2 px-3">Margin %</th>
                        <th className="py-2 px-3">Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trendData.trends?.map((t, idx) => (
                        <tr key={t.period} className="border-b hover:bg-slate-50">
                          <td className="py-2 px-3 font-medium">{t.month}</td>
                          <td className="py-2 px-3 text-right font-mono text-green-600">{formatCurrency(t.revenue)}</td>
                          <td className="py-2 px-3 text-right font-mono text-red-600">{formatCurrency(t.costs)}</td>
                          <td className="py-2 px-3 text-right font-mono text-blue-600">{formatCurrency(t.margin)}</td>
                          <td className={`py-2 px-3 text-right font-mono ${t.margin_pct >= 20 ? 'text-green-600' : t.margin_pct >= 10 ? 'text-amber-600' : 'text-red-600'}`}>
                            {t.margin_pct?.toFixed(1)}%
                          </td>
                          <td className="py-2 px-3 text-center">
                            {idx > 0 && trendData.trends[idx - 1].margin < t.margin ? (
                              <TrendingUp className="h-4 w-4 text-green-500 inline" />
                            ) : idx > 0 && trendData.trends[idx - 1].margin > t.margin ? (
                              <TrendingDown className="h-4 w-4 text-red-500 inline" />
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          ) : (
            <Card className="p-12 text-center">
              <LineChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Loading Trends...</h3>
              <p className="text-muted-foreground">Fetching historical financial data</p>
            </Card>
          )}
        </TabsContent>

        {/* Consolidated/Group Tab */}
        <TabsContent value="consolidated" className="space-y-4">
          <Card className="p-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Building2 className="h-6 w-6" />
                Group Consolidated View
              </h2>
              <Button variant="outline" onClick={loadConsolidated} disabled={loading}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </Card>

          {consolidatedData ? (
            <>
              {/* Group Totals */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <p className="text-sm text-green-700">Group Revenue YTD</p>
                  <p className="text-2xl font-bold text-green-800">{formatCurrency(consolidatedData.total_revenue_ytd)}</p>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <p className="text-sm text-blue-700">Group Net Income</p>
                  <p className="text-2xl font-bold text-blue-800">{formatCurrency(consolidatedData.total_net_income)}</p>
                  <Badge className="mt-1" variant={consolidatedData.overall_margin_pct >= 15 ? "default" : "secondary"}>
                    {consolidatedData.overall_margin_pct?.toFixed(1)}% margin
                  </Badge>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <p className="text-sm text-purple-700">Total Assets</p>
                  <p className="text-2xl font-bold text-purple-800">{formatCurrency(consolidatedData.total_assets)}</p>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <p className="text-sm text-amber-700">Total AR Outstanding</p>
                  <p className="text-2xl font-bold text-amber-800">{formatCurrency(consolidatedData.total_ar)}</p>
                </Card>
              </div>

              {/* Company Breakdown */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Company Performance ({consolidatedData.company_count} Companies)
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-2 px-3">Company</th>
                        <th className="text-right py-2 px-3">Revenue YTD</th>
                        <th className="text-right py-2 px-3">Costs YTD</th>
                        <th className="text-right py-2 px-3">Net Income</th>
                        <th className="text-right py-2 px-3">Margin %</th>
                        <th className="text-right py-2 px-3">AR Balance</th>
                        <th className="text-right py-2 px-3">Assets</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consolidatedData.company_breakdown?.map((company) => (
                        <tr key={company.company_id} className="border-b hover:bg-slate-50">
                          <td className="py-2 px-3 font-medium">{company.company_name}</td>
                          <td className="py-2 px-3 text-right font-mono text-green-600">{formatCurrency(company.revenue_ytd)}</td>
                          <td className="py-2 px-3 text-right font-mono text-red-600">{formatCurrency(company.costs_ytd)}</td>
                          <td className="py-2 px-3 text-right font-mono text-blue-600">{formatCurrency(company.net_income)}</td>
                          <td className={`py-2 px-3 text-right font-mono ${company.margin_pct >= 15 ? 'text-green-600' : company.margin_pct >= 5 ? 'text-amber-600' : 'text-red-600'}`}>
                            {company.margin_pct?.toFixed(1)}%
                          </td>
                          <td className="py-2 px-3 text-right font-mono">{formatCurrency(company.ar_balance)}</td>
                          <td className="py-2 px-3 text-right font-mono">{formatCurrency(company.asset_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-100 font-semibold">
                        <td className="py-2 px-3">Group Total</td>
                        <td className="py-2 px-3 text-right font-mono text-green-700">{formatCurrency(consolidatedData.total_revenue_ytd)}</td>
                        <td className="py-2 px-3 text-right font-mono text-red-700">{formatCurrency(consolidatedData.total_costs_ytd)}</td>
                        <td className="py-2 px-3 text-right font-mono text-blue-700">{formatCurrency(consolidatedData.total_net_income)}</td>
                        <td className="py-2 px-3 text-right font-mono">{consolidatedData.overall_margin_pct?.toFixed(1)}%</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(consolidatedData.total_ar)}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(consolidatedData.total_assets)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>
            </>
          ) : (
            <Card className="p-12 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Loading Consolidated Data...</h3>
              <p className="text-muted-foreground">Aggregating data across all companies</p>
            </Card>
          )}
        </TabsContent>

        {/* Payroll Tab */}
        <TabsContent value="payroll" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Users className="h-6 w-6" />
              Payroll Management
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadPayroll}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={() => setPayrollDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Record Payroll
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Total Payroll Records</p>
              <p className="text-2xl font-bold">{payrollRecords.length}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Total Gross Payable</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(payrollRecords.reduce((sum, p) => sum + (p.total_gross || 0), 0))}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Posted to GL</p>
              <p className="text-2xl font-bold text-blue-600">
                {payrollRecords.filter(p => p.status === 'posted').length}
              </p>
            </Card>
          </div>

          <Card className="p-4">
            <h3 className="font-semibold mb-4">Payroll Records</h3>
            {payrollRecords.length > 0 ? (
              <div className="space-y-3">
                {payrollRecords.map(payroll => (
                  <div key={payroll.id} className="p-4 border rounded-lg hover:bg-slate-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-lg">{payroll.period}</p>
                        <p className="text-sm text-muted-foreground">{payroll.employee_count} employees</p>
                      </div>
                      <Badge className={payroll.status === 'posted' ? 'bg-green-100 text-green-800' : payroll.status === 'approved' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
                        {payroll.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-4 mt-3 text-sm">
                      <div>
                        <p className="text-muted-foreground">Gross</p>
                        <p className="font-mono font-semibold">{formatCurrency(payroll.total_gross)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Deductions</p>
                        <p className="font-mono font-semibold text-red-600">{formatCurrency(payroll.total_deductions)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Net Payable</p>
                        <p className="font-mono font-semibold text-green-600">{formatCurrency(payroll.net_payable)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Employer Cost</p>
                        <p className="font-mono font-semibold">{formatCurrency(payroll.total_employer_cost)}</p>
                      </div>
                    </div>
                    {payroll.status !== 'posted' && (
                      <div className="mt-3 pt-3 border-t flex justify-end">
                        <Button size="sm" onClick={() => handlePostPayroll(payroll.id)}>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Post to GL
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No payroll records yet. Click &quot;Record Payroll&quot; to add one.</p>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Commissions Tab */}
        <TabsContent value="commissions" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Briefcase className="h-6 w-6" />
              Sales Commissions
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadCommissions}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={() => setCommissionDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Record Commission
              </Button>
            </div>
          </div>

          {commissionSummary && (
            <div className="grid grid-cols-4 gap-4">
              <Card className="p-4">
                <p className="text-sm text-muted-foreground">Total Sales Base</p>
                <p className="text-2xl font-bold">{formatCurrency(commissionSummary.total_sales)}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-muted-foreground">Total Commissions</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(commissionSummary.total_commissions)}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-muted-foreground">Total Payable</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(commissionSummary.total_payable)}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-muted-foreground">Salespeople</p>
                <p className="text-2xl font-bold">{commissionSummary.by_salesperson?.length || 0}</p>
              </Card>
            </div>
          )}

          {/* Salesperson Summary */}
          {commissionSummary?.by_salesperson?.length > 0 && (
            <Card className="p-4">
              <h3 className="font-semibold mb-4">Commission by Salesperson</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-2 px-3">Salesperson</th>
                      <th className="text-right py-2 px-3">Sales Base</th>
                      <th className="text-right py-2 px-3">Commission</th>
                      <th className="text-right py-2 px-3">Bonus</th>
                      <th className="text-right py-2 px-3">Total Payable</th>
                      <th className="text-right py-2 px-3">Periods</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissionSummary.by_salesperson.map((sp) => (
                      <tr key={sp.salesperson_id} className="border-b hover:bg-slate-50">
                        <td className="py-2 px-3 font-medium">{sp.salesperson_name}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(sp.total_sales)}</td>
                        <td className="py-2 px-3 text-right font-mono text-green-600">{formatCurrency(sp.total_commission)}</td>
                        <td className="py-2 px-3 text-right font-mono text-amber-600">{formatCurrency(sp.total_bonus)}</td>
                        <td className="py-2 px-3 text-right font-mono text-blue-600 font-semibold">{formatCurrency(sp.total_payable)}</td>
                        <td className="py-2 px-3 text-right">{sp.periods}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Commission Records */}
          <Card className="p-4">
            <h3 className="font-semibold mb-4">Recent Commission Records</h3>
            {commissionRecords.length > 0 ? (
              <div className="space-y-3">
                {commissionRecords.slice(0, 10).map(comm => (
                  <div key={comm.id} className="p-3 border rounded-lg hover:bg-slate-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{comm.salesperson_name}</p>
                        <p className="text-sm text-muted-foreground">{comm.period}</p>
                      </div>
                      <Badge className={comm.status === 'paid' ? 'bg-green-100 text-green-800' : comm.status === 'approved' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
                        {comm.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-4 mt-2 text-sm">
                      <div>
                        <p className="text-muted-foreground">Base Amount</p>
                        <p className="font-mono">{formatCurrency(comm.base_amount)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Rate</p>
                        <p className="font-mono">{comm.commission_rate}%</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Commission</p>
                        <p className="font-mono text-green-600">{formatCurrency(comm.commission_amount)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total Payable</p>
                        <p className="font-mono text-blue-600 font-semibold">{formatCurrency(comm.total_payable)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Briefcase className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No commission records yet. Click &quot;Record Commission&quot; to add one.</p>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* VAT Tab */}
        <TabsContent value="vat" className="space-y-4">
          <Card className="p-4">
            <div className="flex gap-4 items-end">
              <div>
                <Label>Period</Label>
                <Input type="month" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
              </div>
              <Button onClick={loadVatReport} disabled={loading}>
                <Calculator className="h-4 w-4 mr-2" />
                Generate VAT Report
              </Button>
            </div>
          </Card>

          {vatReport && (
            <Card className="p-6">
              <h2 className="text-xl font-bold mb-4">VAT Reconciliation - {vatReport.period}</h2>

              <div className="grid grid-cols-3 gap-6 mb-6">
                <Card className="p-4 bg-green-50">
                  <h3 className="text-sm text-green-700 mb-2">Output VAT (Sales)</h3>
                  <p className="text-2xl font-bold text-green-800">{formatCurrency(vatReport.output_vat?.vat_amount, 2)}</p>
                  <p className="text-xs text-green-600 mt-1">
                    On {formatCurrency(vatReport.output_vat?.taxable_amount)} taxable
                  </p>
                </Card>

                <Card className="p-4 bg-red-50">
                  <h3 className="text-sm text-red-700 mb-2">Input VAT (Purchases)</h3>
                  <p className="text-2xl font-bold text-red-800">{formatCurrency(vatReport.input_vat?.vat_amount, 2)}</p>
                  <p className="text-xs text-red-600 mt-1">
                    On {formatCurrency(vatReport.input_vat?.taxable_amount)} taxable
                  </p>
                </Card>

                <Card className={`p-4 ${vatReport.net_vat?.amount >= 0 ? 'bg-amber-50' : 'bg-blue-50'}`}>
                  <h3 className={`text-sm ${vatReport.net_vat?.amount >= 0 ? 'text-amber-700' : 'text-blue-700'} mb-2`}>
                    Net VAT {vatReport.net_vat?.status}
                  </h3>
                  <p className={`text-2xl font-bold ${vatReport.net_vat?.amount >= 0 ? 'text-amber-800' : 'text-blue-800'}`}>
                    {formatCurrency(Math.abs(vatReport.net_vat?.amount), 2)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Due: {vatReport.net_vat?.due_date}
                  </p>
                </Card>
              </div>

              <div className="text-sm text-muted-foreground">
                <p>VAT Rate: {vatReport.vat_rate}%</p>
                <p>Invoice Count: {vatReport.output_vat?.invoice_count}</p>
                <p>Purchase Transactions: {vatReport.input_vat?.transaction_count}</p>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* Adjustments Tab (Credit/Debit Notes) */}
        <TabsContent value="adjustments" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Credit & Debit Notes</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadCreditDebitNotes}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={() => { setNoteType('credit'); setNoteDialogOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Credit Note
              </Button>
              <Button onClick={() => { setNoteType('debit'); setNoteDialogOpen(true); }} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Debit Note
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Credit Notes */}
            <Card className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <ArrowDownRight className="h-5 w-5 text-green-600" />
                Credit Notes (Customer Credits)
              </h3>
              <div className="space-y-3">
                {creditNotes.map(note => (
                  <div key={note.id} className="p-3 border rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{note.credit_note_number}</p>
                        <p className="text-sm text-muted-foreground">Invoice: {note.invoice_number}</p>
                      </div>
                      <Badge className={note.status === 'approved' ? 'bg-green-100' : 'bg-amber-100'}>
                        {note.status}
                      </Badge>
                    </div>
                    <div className="mt-2 flex justify-between">
                      <span className="text-sm">{note.reason}</span>
                      <span className="font-mono font-semibold text-green-600">
                        -{formatCurrency(note.credit_amount)}
                      </span>
                    </div>
                  </div>
                ))}
                {creditNotes.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No credit notes</p>
                )}
              </div>
            </Card>

            {/* Debit Notes */}
            <Card className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <ArrowUpRight className="h-5 w-5 text-red-600" />
                Debit Notes (Additional Charges)
              </h3>
              <div className="space-y-3">
                {debitNotes.map(note => (
                  <div key={note.id} className="p-3 border rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{note.debit_note_number}</p>
                        <p className="text-sm text-muted-foreground">Invoice: {note.invoice_number}</p>
                      </div>
                      <Badge className={note.status === 'approved' ? 'bg-green-100' : 'bg-amber-100'}>
                        {note.status}
                      </Badge>
                    </div>
                    <div className="mt-2 flex justify-between">
                      <span className="text-sm">{note.reason}</span>
                      <span className="font-mono font-semibold text-red-600">
                        +{formatCurrency(note.debit_amount)}
                      </span>
                    </div>
                  </div>
                ))}
                {debitNotes.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No debit notes</p>
                )}
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {noteType === 'credit' ? <ArrowDownRight className="h-5 w-5 text-green-600" /> : <ArrowUpRight className="h-5 w-5 text-red-600" />}
              Create {noteType === 'credit' ? 'Credit' : 'Debit'} Note
            </DialogTitle>
            <DialogDescription>
              {noteType === 'credit' ? 'Reduce amount owed by customer' : 'Add additional charges to invoice'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Invoice *</Label>
              <Select
                value={newNote.invoice_id}
                onValueChange={(v) => setNewNote({...newNote, invoice_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select invoice" />
                </SelectTrigger>
                <SelectContent>
                  {invoices.map(inv => (
                    <SelectItem key={inv.id} value={inv.id}>
                      {inv.invoice_number} - {inv.client_name} ({formatCurrency(inv.total)} SAR)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (SAR) *</Label>
                <Input
                  type="number"
                  value={newNote.amount || ''}
                  onChange={(e) => setNewNote({...newNote, amount: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Reason Code</Label>
                <Select
                  value={newNote.reason_code}
                  onValueChange={(v) => setNewNote({...newNote, reason_code: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {noteType === 'credit' ? (
                      <>
                        <SelectItem value="RETURN">Return</SelectItem>
                        <SelectItem value="DISCOUNT">Discount</SelectItem>
                        <SelectItem value="ERROR">Billing Error</SelectItem>
                        <SelectItem value="SERVICE">Service Issue</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="ADDITIONAL">Additional Services</SelectItem>
                        <SelectItem value="CORRECTION">Undercharge Correction</SelectItem>
                        <SelectItem value="LATE_FEE">Late Fee</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Reason *</Label>
              <Input
                value={newNote.reason}
                onChange={(e) => setNewNote({...newNote, reason: e.target.value})}
                placeholder="Brief reason for adjustment"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={newNote.description}
                onChange={(e) => setNewNote({...newNote, description: e.target.value})}
                rows={2}
                placeholder="Additional details..."
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleCreateNote}
                disabled={!newNote.invoice_id || !newNote.amount || !newNote.reason}
                className={noteType === 'credit' ? 'bg-green-600' : 'bg-red-600'}
              >
                Create {noteType === 'credit' ? 'Credit' : 'Debit'} Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payroll Dialog */}
      <Dialog open={payrollDialogOpen} onOpenChange={setPayrollDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Record Payroll
            </DialogTitle>
            <DialogDescription>
              Record monthly payroll for employees
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period *</Label>
                <Input
                  type="month"
                  value={newPayroll.period}
                  onChange={(e) => setNewPayroll({...newPayroll, period: e.target.value})}
                />
              </div>
              <div>
                <Label>Employee Count *</Label>
                <Input
                  type="number"
                  value={newPayroll.employee_count || ''}
                  onChange={(e) => setNewPayroll({...newPayroll, employee_count: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div>
              <Label>Total Gross Salary (SAR) *</Label>
              <Input
                type="number"
                value={newPayroll.total_gross || ''}
                onChange={(e) => setNewPayroll({...newPayroll, total_gross: parseFloat(e.target.value) || 0})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Basic Salary</Label>
                <Input
                  type="number"
                  value={newPayroll.basic_salary || ''}
                  onChange={(e) => setNewPayroll({...newPayroll, basic_salary: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Housing Allowance</Label>
                <Input
                  type="number"
                  value={newPayroll.housing_allowance || ''}
                  onChange={(e) => setNewPayroll({...newPayroll, housing_allowance: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Transport Allowance</Label>
                <Input
                  type="number"
                  value={newPayroll.transport_allowance || ''}
                  onChange={(e) => setNewPayroll({...newPayroll, transport_allowance: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Other Deductions</Label>
                <Input
                  type="number"
                  value={newPayroll.other_deductions || ''}
                  onChange={(e) => setNewPayroll({...newPayroll, other_deductions: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setPayrollDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleRecordPayroll}
                disabled={!newPayroll.period || !newPayroll.employee_count || !newPayroll.total_gross}
              >
                Record Payroll
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Commission Dialog */}
      <Dialog open={commissionDialogOpen} onOpenChange={setCommissionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-green-600" />
              Record Commission
            </DialogTitle>
            <DialogDescription>
              Record sales commission for a salesperson
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Salesperson ID *</Label>
                <Input
                  value={newCommission.salesperson_id}
                  onChange={(e) => setNewCommission({...newCommission, salesperson_id: e.target.value})}
                  placeholder="e.g., SP001"
                />
              </div>
              <div>
                <Label>Salesperson Name *</Label>
                <Input
                  value={newCommission.salesperson_name}
                  onChange={(e) => setNewCommission({...newCommission, salesperson_name: e.target.value})}
                  placeholder="Full name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period *</Label>
                <Input
                  type="month"
                  value={newCommission.period}
                  onChange={(e) => setNewCommission({...newCommission, period: e.target.value})}
                />
              </div>
              <div>
                <Label>Commission Rate (%)</Label>
                <Input
                  type="number"
                  value={newCommission.commission_rate || ''}
                  onChange={(e) => setNewCommission({...newCommission, commission_rate: parseFloat(e.target.value) || 5})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Base Sales Amount (SAR) *</Label>
                <Input
                  type="number"
                  value={newCommission.base_amount || ''}
                  onChange={(e) => setNewCommission({...newCommission, base_amount: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Bonus Amount (SAR)</Label>
                <Input
                  type="number"
                  value={newCommission.bonus_amount || ''}
                  onChange={(e) => setNewCommission({...newCommission, bonus_amount: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={newCommission.notes}
                onChange={(e) => setNewCommission({...newCommission, notes: e.target.value})}
                rows={2}
                placeholder="Additional notes..."
              />
            </div>

            {newCommission.base_amount > 0 && (
              <Card className="p-3 bg-green-50">
                <p className="text-sm text-green-700">
                  Calculated Commission: <span className="font-bold">{formatCurrency((newCommission.base_amount * newCommission.commission_rate / 100) + (newCommission.bonus_amount || 0))} SAR</span>
                </p>
              </Card>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setCommissionDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleRecordCommission}
                disabled={!newCommission.salesperson_id || !newCommission.salesperson_name || !newCommission.period || !newCommission.base_amount}
                className="bg-green-600"
              >
                Record Commission
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
